import { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaTimes } from 'react-icons/fa';
import SignaturePad from 'signature_pad';

import './Signature.scss';

export interface SignatureHandle {
    getDataUrl: () => string | null;
    isEmpty: () => boolean;
    clear: () => void;
}

const Signature = forwardRef<SignatureHandle>(function Signature(_props, ref) {
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const resizeHandlerRef = useRef<(() => void) | null>(null);

    const [validSig, setValidSig] = useState(false);

    useImperativeHandle(ref, () => ({
        getDataUrl: () => {
            if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return null;
            return signaturePadRef.current.toDataURL('image/jpeg', 0.7);
        },
        isEmpty: () => signaturePadRef.current?.isEmpty() ?? true,
        clear: () => {
            signaturePadRef.current?.clear();
            setValidSig(false);
        }
    }));

    const initSignature = useCallback((canvas: HTMLCanvasElement) => {
        const resizeCanvas = () => {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            // Preserve drawn strokes across resize (orientation change, keyboard popup)
            const data = signaturePadRef.current?.toData();
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            const ctx = canvas.getContext('2d');
            ctx?.scale(ratio, ratio);
            signaturePadRef.current?.clear();
            if (data && data.length > 0) {
                signaturePadRef.current?.fromData(data);
            }
        };

        const checkValid = () => {
            const empty = signaturePadRef.current?.isEmpty() ?? true;
            setValidSig(!empty);
        };

        if (signaturePadRef.current) {
            signaturePadRef.current.off();
        }

        const pad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)'
        });
        signaturePadRef.current = pad;
        pad.addEventListener('endStroke', checkValid);

        if (resizeHandlerRef.current) {
            window.removeEventListener('resize', resizeHandlerRef.current);
        }
        resizeHandlerRef.current = resizeCanvas;
        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();
    }, []);

    useEffect(() => {
        const canvasCheck = setInterval(() => {
            const canvas = document.getElementById('signature-canvas') as HTMLCanvasElement | null;
            if (canvas) {
                initSignature(canvas);
                clearInterval(canvasCheck);
            }
        }, 100);

        return () => {
            clearInterval(canvasCheck);
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current);
            }
            if (signaturePadRef.current) {
                signaturePadRef.current.off();
            }
        };
    }, [initSignature]);

    const clearCanvas = () => {
        signaturePadRef.current?.clear();
        setValidSig(false);
    };

    return (
        <div id="signature-container">
            <canvas id="signature-canvas">
                Signature
            </canvas>
            <hr className="dashed" />
            {validSig && <button className="danger small" onClick={clearCanvas}><FaTimes></FaTimes></button>}
        </div>
    );
});

export default Signature;